import { produce } from 'immer';

import { NotificationCategory, NotificationChannel, NotificationSettings } from '../types';

/**
 * Toggle a specific topic + channel combination
 */
export function toggleSetting(settings: NotificationSettings, topicId: string, channel: NotificationChannel): NotificationSettings {
	return produce(settings, (draft) => {
		const setting = draft.find((s) => s.topicId === topicId && s.channel === channel);
		if (setting) {
			setting.enabled = !setting.enabled;
		}
	});
}

/**
 * Toggle all settings in a category for a specific channel.
 * Only toggles topics that are present in the provided categories (visible topics).
 */
export function toggleCategoryChannel(
	settings: NotificationSettings,
	categoryId: string,
	channel: NotificationChannel,
	categories: NotificationCategory[]
): NotificationSettings {
	// Find all topic IDs in this category (from the visible categories)
	const category = categories.find((c) => c.id === categoryId);
	if (!category) return settings;

	const topicIds = category.topics.map((t) => t.id);

	// Find settings for this category + channel combination
	const categoryChannelSettings = settings.filter((s) => topicIds.includes(s.topicId) && s.channel === channel);

	// Check if all are enabled
	const allEnabled = categoryChannelSettings.length > 0 && categoryChannelSettings.every((s) => s.enabled);

	// Toggle all to opposite state
	return produce(settings, (draft) => {
		draft.forEach((setting) => {
			if (topicIds.includes(setting.topicId) && setting.channel === channel) {
				setting.enabled = !allEnabled;
			}
		});
	});
}

/**
 * Check if all settings in a category for a channel are enabled.
 * Only considers topics that are present in the provided categories (visible topics).
 */
export function isCategoryChannelChecked(
	settings: NotificationSettings,
	categoryId: string,
	channel: NotificationChannel,
	categories: NotificationCategory[]
): boolean {
	const category = categories.find((c) => c.id === categoryId);
	if (!category) return false;

	const topicIds = category.topics.map((t) => t.id);
	const categoryChannelSettings = settings.filter((s) => topicIds.includes(s.topicId) && s.channel === channel);

	return categoryChannelSettings.length > 0 && categoryChannelSettings.every((s) => s.enabled);
}

/**
 * Check if category + channel checkbox should be indeterminate (some but not all enabled).
 * Only considers topics that are present in the provided categories (visible topics).
 */
export function isCategoryChannelIndeterminate(
	settings: NotificationSettings,
	categoryId: string,
	channel: NotificationChannel,
	categories: NotificationCategory[]
): boolean {
	const category = categories.find((c) => c.id === categoryId);
	if (!category) return false;

	const topicIds = category.topics.map((t) => t.id);
	const categoryChannelSettings = settings.filter((s) => topicIds.includes(s.topicId) && s.channel === channel);

	if (categoryChannelSettings.length === 0) return false;

	const enabledCount = categoryChannelSettings.filter((s) => s.enabled).length;
	return enabledCount > 0 && enabledCount < categoryChannelSettings.length;
}

/**
 * Get the setting value for a specific topic + channel
 */
export function getSetting(settings: NotificationSettings, topicId: string, channel: NotificationChannel): boolean {
	const setting = settings.find((s) => s.topicId === topicId && s.channel === channel);
	return setting?.enabled ?? false;
}
